import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { firstValueFrom } from 'rxjs';
import { FileService } from 'src/app/services/api/file.service';
import {
  PassengerDocumentType,
  resolvePassengerDocument
} from 'src/shared/lib';

@Component({
  selector: 'app-pasajeros-dialog',
  templateUrl: './pasajeros-dialog.component.html',
  styleUrls: ['./pasajeros-dialog.component.css']
})
export class PasajerosDialogComponent implements OnInit {
  displayedColumns: string[] = [
    'Pax',
    'updatedAt',
    'firstName',
    'lastName',
    'residesInPeru',
    'documentType',
    'documentNumber',
    'dateOfPass',
    'dateOfBirth',
    'gender',
    'edad',
    'talla',
    'peso',
    'altura',
    'grupo',
    'email',
    'phone',
    'country',
    'restriccionesAlimentarias',
    'alergias',
    'condicionesMedicas',
    'isTC'
  ];

  isLoading = false;
  searchValue = '';
  expectedPaxQuantity = 0;
  expectedTcQuantity = 0;
  pasajeros: any;
  dataSource = new MatTableDataSource<any>();
  // passengerSource = new MatTableDataSource<bibliaModel>();

  constructor(
    private readonly fileService: FileService,
    public dialogRef: MatDialogRef<PasajerosDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public paxList: any
  ) {}

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) {
      this.sort = sort;
      if (this.dataSource) {
        this.dataSource.sort = sort;
      }
    }
  }
  sort!: MatSort;

  get hasData(): boolean {
    return this.dataSource.data.length > 0;
  }

  get totalPassengers(): number {
    return this.dataSource.data.length;
  }

  get expectedTotalPassengers(): number {
    return this.expectedPaxQuantity + this.expectedTcQuantity;
  }

  get paxCount(): number {
    return this.dataSource.data.filter((item) => item?.pasajero?.isTC === false)
      .length;
  }

  get tcCount(): number {
    return this.dataSource.data.filter((item) => item?.pasajero?.isTC === true)
      .length;
  }

  ngOnInit(): void {
    console.log({
      paxList: this.paxList
    });
    this.isLoading = true;
    this.dataSource.filterPredicate = this.filterPassengers;
    const normalizedPaxList = this.normalizeDialogData();

    if (!Array.isArray(normalizedPaxList) || normalizedPaxList.length === 0) {
      this.isLoading = false;
      return;
    }

    // Identificar pasajeros activos que tengan un identificador válido
    const activePassengers = normalizedPaxList.filter(
      (pax: any) =>
        pax?.estado !== false && (pax?.passengerId || pax?.pasajero?.id)
    );

    if (activePassengers.length === 0) {
      this.setPassengerData(normalizedPaxList);
      this.isLoading = false;
      return;
    }

    // Un pasajero se considera con data completa si tiene toda la info para poblar la tabla
    const isPassengerComplete = (pax: any): boolean => {
      const p = pax?.pasajero;
      if (!p || typeof p !== 'object') {
        return false;
      }

      const tableFields = [
        'updatedAt',
        'firstName',
        'lastName',
        'residesInPeru',
        'documentType',
        'documentNumber',
        'passportNumber',
        'gender',
        'age',
        'size',
        'weight',
        'height',
        'dateOfBirth',
        'dateOfPass',
        'email',
        'phone',
        'country',
        'restriccionesAlimentarias',
        'alergias',
        'condicionesMedicas',
        'isTC'
      ];

      const hasAllTableFields = tableFields.every(
        (field) => field in p && p[field] !== undefined
      );
      return hasAllTableFields;
    };

    // Identificar IDs únicos de pasajeros a los que les faltan datos completos
    const passengerIdsToFetch = Array.from(
      new Set(
        activePassengers
          .filter((pax: any) => !isPassengerComplete(pax))
          .map((pax: any) => pax.passengerId || pax.pasajero?.id)
          .filter(Boolean)
      )
    );

    // Si todos los pasajeros ya tienen sus datos completos, no es necesario consultar la API
    if (passengerIdsToFetch.length === 0) {
      this.setPassengerData(normalizedPaxList);
      this.isLoading = false;
      return;
    }

    firstValueFrom(
      this.fileService.getPassengersByIds({
        passengerIds: passengerIdsToFetch,
        passengerFields: {
          id: true,
          firstName: true,
          lastName: true,
          phone: true,
          email: true,
          country: true,
          condicionesMedicas: true,
          dateOfBirth: true,
          age: true,
          weight: true,
          height: true,
          size: true,
          alergias: true,
          restriccionesAlimentarias: true,
          residesInPeru: true,
          documentType: true,
          documentNumber: true,
          passportNumber: true,
          dateOfPass: true,
          doe: true,
          gender: true,
          isTC: true,
          createdAt: true,
          updatedAt: true
        }
      })
    )
      .then((getPassengersByIdsResponse) => {
        const fetchedPassengers: any[] =
          getPassengersByIdsResponse?.getPassengers ?? [];
        const passengerMap = new Map<string, any>(
          fetchedPassengers.map((p: any) => [p.id, p])
        );

        const enrichedList = normalizedPaxList
          .filter((pax: any) => pax?.estado !== false)
          .map((pax: any) => {
            const pId = pax.passengerId || pax.pasajero?.id;
            const fetched = passengerMap.get(pId);
            return {
              ...pax,
              passengerId: pId,
              pasajero: {
                ...(pax.pasajero || {}),
                ...(fetched || {})
              }
            };
          });

        this.setPassengerData(enrichedList);
      })
      .catch((error) => {
        console.error('Error al cargar datos detallados de pasajeros:', error);
        this.setPassengerData(normalizedPaxList);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  applySearch(value: string): void {
    this.searchValue = value;
    this.dataSource.filter = value.trim().toLowerCase();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  closeDialogWindow() {
    this.dialogRef.close();
  }

  private setPassengerData(passengers: any[]): void {
    const normalized = this.normalizePassengerRows(passengers);
    this.updateCounters(normalized);
    this.dataSource = new MatTableDataSource(normalized);
    this.dataSource.filterPredicate = this.filterPassengers;
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    this.applySearch(this.searchValue);
  }

  private updateCounters(passengers: any[]): void {
    const hasSummary =
      this.paxList?.summary?.paxQuantity != null ||
      this.paxList?.paxQuantity != null;

    if (!hasSummary) {
      this.expectedPaxQuantity = passengers.filter(
        (passenger: any) => passenger?.pasajero?.isTC === false
      ).length;
      this.expectedTcQuantity = passengers.filter(
        (passenger: any) => passenger?.pasajero?.isTC === true
      ).length;
    }
  }

  private normalizePassengerRows(rows: any[]): any[] {
    return rows.map((row) => {
      const pasajero = row?.pasajero
        ? this.normalizePasajero(row.pasajero)
        : row.pasajero;

      return {
        ...row,
        passengerId: row?.passengerId || pasajero?.id,
        estado: row?.estado ?? true,
        pasajero,
        documentInfo: resolvePassengerDocument(pasajero)
      };
    });
  }

  /** Fallback dob/doe solo para ruta legacy biblia (getPassengersByIds). CORE usa dateOfBirth. */
  private normalizePasajero(pasajero: any): any {
    return {
      ...pasajero,
      dateOfBirth: pasajero.dateOfBirth ?? pasajero.dob ?? null,
      dateOfPass: pasajero.dateOfPass ?? pasajero.doe ?? null
    };
  }

  private normalizeDialogData(): any[] {
    const rawData =
      this.paxList?.passengers || this.paxList?.summary
        ? this.paxList
        : (this.paxList?.paxList ?? this.paxList);

    let rows: any[] = [];
    if (Array.isArray(rawData)) {
      this.expectedPaxQuantity = rawData.filter(
        (pax: any) => pax?.pasajero?.isTC === false
      ).length;
      this.expectedTcQuantity = rawData.filter(
        (pax: any) => pax?.pasajero?.isTC === true
      ).length;
      rows = rawData;
    } else {
      this.expectedPaxQuantity =
        rawData?.summary?.paxQuantity ?? rawData?.paxQuantity ?? 0;
      this.expectedTcQuantity =
        rawData?.summary?.tcQuantity ?? rawData?.tcQuantity ?? 0;
      rows = rawData?.passengers ?? rawData?.paxList ?? [];
    }

    return rows.map((row) => ({
      ...row,
      passengerId: row?.passengerId || row?.pasajero?.id,
      estado: row?.estado ?? true
    }));
  }

  private readonly filterPassengers = (item: any, filter: string): boolean => {
    if (!filter) {
      return true;
    }

    const passenger = item?.pasajero ?? {};
    const documentInfo =
      item?.documentInfo ?? resolvePassengerDocument(passenger);
    const searchableText = [
      item?.Pax,
      item?.grupo,
      documentInfo.residesInPeru ? 'si reside peru' : 'no reside peru',
      documentInfo.documentType,
      this.formatDocumentType(documentInfo.documentType),
      documentInfo.documentNumber,
      documentInfo.passportNumber,
      passenger?.firstName,
      passenger?.lastName,
      passenger?.email,
      passenger?.phone,
      passenger?.gender,
      passenger?.country,
      passenger?.condicionesMedicas,
      passenger?.alergias,
      passenger?.restriccionesAlimentarias,
      passenger?.isTC === true
        ? 'tc'
        : passenger?.isTC === false
          ? 'pax'
          : 'n/a'
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(filter);
  };

  formatDocumentType(value: PassengerDocumentType | null | undefined): string {
    if (!value) {
      return '-';
    }

    return value === 'PASSPORT' ? 'Pasaporte' : value;
  }
  formatDate(value: string | Date | null | undefined): string {
    if (!value) {
      return '-';
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
      return '-';
    }

    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(parsedDate);
  }

  formatDateTime(value: string | Date | null | undefined): string {
    if (!value) {
      return '-';
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
      return '-';
    }

    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsedDate);
  }

  formatGender(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const normalizedGender = value.trim().toLowerCase();

    if (
      normalizedGender === 'm' ||
      normalizedGender === 'masculino' ||
      normalizedGender === 'male'
    ) {
      return 'Masculino';
    }

    if (
      normalizedGender === 'f' ||
      normalizedGender === 'femenino' ||
      normalizedGender === 'female'
    ) {
      return 'Femenino';
    }

    return value;
  }
}
